import { fromMarkdown } from 'mdast-util-from-markdown';

export const SUGGESTION_SCHEME = 'suggestion:';

const SUGGESTION_LINK_PATTERN = /\[([^\]]+)\]\(suggestion:((?:[^()]|\([^()]*\))*)\)/g;

// Le modèle écrit les options sous la forme `[libellé](suggestion:message à envoyer)`. Or une
// destination de lien markdown ne peut pas contenir d'espaces : sans traitement, remark ne la
// parse pas comme un lien et l'affiche en texte brut. On URL-encode donc la destination en amont
// du rendu pour en faire un lien valide. Le libellé (entre crochets) n'est jamais touché.
export const normalizeSuggestionLinks = (content) =>
    content.replace(
        SUGGESTION_LINK_PATTERN,
        (_match, label, payload) => `[${label}](${SUGGESTION_SCHEME}${encodeURIComponent(payload.trim())})`
    );

export const getSuggestionQuestions = (content) => {
    const markdown = normalizeSuggestionLinks(content);
    const blocks = fromMarkdown(markdown).children;
    const questions = [];

    blocks.forEach((block, index) => {
        const previousBlock = blocks[index - 1];

        if (block.type === 'list' && block.children.every(isOptionOnly)) {
            const introBlock =
                previousBlock?.type === 'paragraph' && !hasSuggestionLinks(previousBlock) ? previousBlock : undefined;
            questions.push(toQuestion(introBlock, block, introBlock ? [introBlock, block] : [block]));
            return;
        }

        if (block.type === 'list') {
            block.children.filter(hasSuggestionLinks).forEach((item) => questions.push(toQuestion(item, item, [item])));
            return;
        }

        if (hasSuggestionLinks(block)) {
            questions.push(toQuestion(block, block, [block]));
        }
    });

    return {
        questions: questions.map(({ text, choices }) => ({ text, choices })),
        remainingMarkdown: removeNodes(
            markdown,
            questions.flatMap(({ nodes }) => nodes)
        ),
    };
};

const isSuggestionLink = (node) => node.type === 'link' && node.url.startsWith(SUGGESTION_SCHEME);

const getSuggestionLinks = (node) =>
    isSuggestionLink(node) ? [node] : (node.children ?? []).flatMap(getSuggestionLinks);

const hasSuggestionLinks = (node) => getSuggestionLinks(node).length > 0;

const getNodeText = (node) => node.value ?? (node.children ?? []).map(getNodeText).join('');

const getTextOutsideSuggestionLinks = (node) =>
    isSuggestionLink(node) ? '' : (node.value ?? (node.children ?? []).map(getTextOutsideSuggestionLinks).join(''));

const getTextBeforeSuggestionLink = (node) => {
    if (isSuggestionLink(node)) {
        return { text: '', hasReachedLink: true };
    }

    if (node.value !== undefined) {
        return { text: node.value, hasReachedLink: false };
    }

    let text = '';
    for (const child of node.children ?? []) {
        const childText = getTextBeforeSuggestionLink(child);
        text += childText.text;
        if (childText.hasReachedLink) {
            return { text, hasReachedLink: true };
        }
    }

    return { text, hasReachedLink: false };
};

const getQuestionText = (node) =>
    getTextBeforeSuggestionLink(node)
        .text.replace(/[\s:]+$/, '')
        .trim() || getNodeText(node).trim();

const isOptionOnly = (listItem) =>
    hasSuggestionLinks(listItem) && !/[^\s.,;:?!()-]/.test(getTextOutsideSuggestionLinks(listItem));

const decodeSuggestionMessage = (url) => {
    const payload = url.slice(SUGGESTION_SCHEME.length);
    try {
        return decodeURIComponent(payload).trim();
    } catch {
        return payload.trim();
    }
};

const toChoice = (link) => {
    const label = getNodeText(link).trim();

    return {
        label: label.charAt(0).toLocaleUpperCase() + label.slice(1),
        message: decodeSuggestionMessage(link.url) || label,
    };
};

const toQuestion = (textNode, choicesNode, nodes) => {
    const choices = getSuggestionLinks(choicesNode).map(toChoice);

    return {
        text: textNode ? getQuestionText(textNode) : '',
        choices: choices.filter(
            (choice, index) => choices.findIndex(({ message }) => message === choice.message) === index
        ),
        nodes,
    };
};

const removeNodes = (markdown, nodes) => {
    const ranges = nodes
        .map(({ position }) => [position.start.offset, position.end.offset])
        .sort(([startA], [startB]) => startA - startB);
    let cursor = 0;
    let remaining = '';

    ranges.forEach(([start, end]) => {
        remaining += markdown.slice(cursor, start);
        cursor = end;
    });

    return (remaining + markdown.slice(cursor)).trim();
};
